import React, { useEffect, useState } from 'react';
import { Form, Input, Select, DatePicker, InputNumber, Radio, Checkbox, Divider, Alert, Spin } from 'antd';
import { CustomerFormFieldsProps, FieldGroupDTO, FieldMetadataDTO } from '../types';
import { useOnboardingStore } from '../stores/onboarding-store';
import {useGetFieldConfigurationForCategoryGrouped} from "@/shared/api/client/field-configuration-controller";

const { TextArea } = Input;
const { Option } = Select;

const CustomerFormFields: React.FC<CustomerFormFieldsProps> = ({
  categoryId,
  customerType,
  formData,
  onFieldChange,
  errors = {},
  readonly = false,
}) => {
  const [form] = Form.useForm();
  const { markStepCompleted, markStepIncomplete } = useOnboardingStore();

  const {
    data: fieldConfiguration,
    isLoading,
    error,
  } = useGetFieldConfigurationForCategoryGrouped(categoryId, {
    query: {
      enabled: !!categoryId,
    },
  });


  useEffect(() => {
    if (formData) {
      form.setFieldsValue(formData);
    }
  }, [formData, form]);

  const handleFieldChange = (fieldName: string, value: any) => {
    onFieldChange(fieldName, value);
    form.setFieldsValue({ [fieldName]: value });
  };

  const validateFields = async () => {
    try {
      await form.validateFields();
      markStepCompleted(0); // Assuming this is step 0
      return true;
    } catch (error) {
      markStepIncomplete(0);
      return false;
    }
  };

  const renderField = (field: FieldMetadataDTO, groupKey: string) => {
    if (!field.fieldName) return null;
    const fieldKey = `${groupKey}.${field.fieldName}`;
    const fieldValue = formData[field.fieldName];
    const hasError = errors[field.fieldName];

    const commonProps = {
      placeholder: field.placeholder || `Enter ${field.label}`,
      disabled: readonly,
      onChange: (value: any) => handleFieldChange(field.fieldName!, value),
    };

    switch (field.type?.toLowerCase()) {
      case 'text':
      case 'string':
        return (
          <Input
            {...commonProps}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.fieldName!, e.target.value)}
          />
        );

      case 'email':
        return (
          <Input
            {...commonProps}
            type="email"
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.fieldName!, e.target.value)}
          />
        );

      case 'phone':
      case 'tel':
        return (
          <Input
            {...commonProps}
            type="tel"
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.fieldName!, e.target.value)}
          />
        );

      case 'number':
      case 'integer':
        return (
          <InputNumber
            {...commonProps}
            value={fieldValue}
            onChange={(value) => handleFieldChange(field.fieldName!, value)}
            style={{ width: '100%' }}
          />
        );

      case 'date':
        return (
          <DatePicker
            {...commonProps}
            value={fieldValue}
            onChange={(date) => handleFieldChange(field.fieldName!, date)}
            style={{ width: '100%' }}
          />
        );

      case 'textarea':
        return (
          <TextArea
            {...commonProps}
            rows={4}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.fieldName!, e.target.value)}
          />
        );

      case 'select':
        return (
          <Select
            {...commonProps}
            value={fieldValue}
            onChange={(value) => handleFieldChange(field.fieldName!, value)}
            placeholder={`Select ${field.label}`}
          >
            {/* Options would be populated from field.selectOptions if available */}
            <Option value="option1">Option 1</Option>
            <Option value="option2">Option 2</Option>
          </Select>
        );

      case 'radio':
        return (
          <Radio.Group
            {...commonProps}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.fieldName!, e.target.value)}
          >
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </Radio.Group>
        );

      case 'checkbox':
        return (
          <Checkbox
            {...commonProps}
            checked={fieldValue}
            onChange={(e) => handleFieldChange(field.fieldName!, e.target.checked)}
          >
            {field.label}
          </Checkbox>
        );

      default:
        return (
          <Input
            {...commonProps}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.fieldName!, e.target.value)}
          />
        );
    }
  };

  const renderFieldGroup = (group: FieldGroupDTO) => {
    if (!group.isVisible || !group.fields || group.fields.length === 0) {
      return null;
    }

    const sortedFields = [...group.fields].sort((a, b) => 
      (a.displayOrder || 0) - (b.displayOrder || 0)
    );

    return (
      <div key={group.groupKey} className="field-group">
        <Divider orientation="left" className="group-divider">
          <span className="group-title">
            {group.icon && <span className="group-icon">{group.icon}</span>}
            {group.groupName}
          </span>
        </Divider>
        
        {group.description && (
          <p className="group-description">{group.description}</p>
        )}

        <div className="group-fields">
          {sortedFields.map((field) => (
            <Form.Item
              key={field.fieldName}
              name={field.fieldName}
              label={field.label}
              rules={[
                {
                  required: field.defaultRequired,
                  message: `${field.label} is required`,
                },
              ]}
              help={field.helpText}
              validateStatus={field.fieldName && errors[field.fieldName] ? 'error' : ''}
              hasFeedback
            >
              {renderField(field, group.groupKey ?? '')}
            </Form.Item>
          ))}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="form-loading">
        <Spin size="large" />
        <p>Loading field configuration...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Fields"
        description="Unable to load field configuration for this category."
        type="error"
        showIcon
      />
    );
  }

  if (!fieldConfiguration || !fieldConfiguration.fieldGroups) {
    return (
      <Alert
        message="No Fields Configured"
        description="No field configuration found for this customer category."
        type="info"
        showIcon
      />
    );
  }

  const sortedGroups = [...fieldConfiguration.fieldGroups].sort((a, b) => 
    (a.displayOrder || 0) - (b.displayOrder || 0)
  );

  return (
    <div className="customer-form-fields">
      <Form
        form={form}
        layout="vertical"
        onValuesChange={validateFields}
        className="onboarding-form"
      >
        {sortedGroups.map(renderFieldGroup)}
      </Form>
    </div>
  );
};

export default CustomerFormFields;
